/** postMessage bridge origins — protocol + host (+ port), no path/query/hash. */
const BRIDGE_ORIGIN_PATTERN = /^https?:\/\/[^/?#]+$/;

export interface ResolveBridgeOriginOptions {
  envValue: string | undefined;
  envName: string;
  isProduction: boolean;
  /** Used only when `envValue` is unset and `isProduction` is false. */
  devFallback?: string;
}

/**
 * Normalize a bridge origin (trim, strip trailing slashes).
 * Returns null when the value is not a bare origin URL.
 */
export function normalizeBridgeOrigin(raw: string): string | null {
  const trimmed = raw.trim().replace(/\/+$/, '');
  if (!trimmed || !BRIDGE_ORIGIN_PATTERN.test(trimmed)) {
    return null;
  }
  return trimmed;
}

/** Strict equality gate for inbound postMessage listeners. */
export function isStrictBridgeOrigin(incoming: string, configured: string): boolean {
  const normalizedIncoming = normalizeBridgeOrigin(incoming);
  const normalizedConfigured = normalizeBridgeOrigin(configured);
  if (!normalizedIncoming || !normalizedConfigured) {
    return false;
  }
  return normalizedIncoming === normalizedConfigured;
}

/**
 * Resolve the single canonical bridge origin from env.
 * Rejects comma-separated lists and invalid URLs. Fails closed in production.
 */
export function resolveBridgeOrigin(options: ResolveBridgeOriginOptions): string {
  const { envValue, envName, isProduction, devFallback } = options;
  const raw = envValue?.trim();

  if (raw) {
    if (raw.includes(',')) {
      throw new Error(
        `${envName} must be a single origin (comma-separated values are not allowed)`,
      );
    }
    const normalized = normalizeBridgeOrigin(raw);
    if (!normalized) {
      throw new Error(
        `${envName} must be a bare origin (e.g. https://studio.example.com or http://localhost:3000)`,
      );
    }
    return normalized;
  }

  if (isProduction) {
    throw new Error(`${envName} is required in production`);
  }

  if (devFallback) {
    const normalized = normalizeBridgeOrigin(devFallback);
    if (normalized) {
      return normalized;
    }
  }

  throw new Error(`${envName} is required (set it in .env for local development)`);
}
